import { CreditCard } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { useOnboardingStore } from '../../../store/onboarding.store';

interface HasPaymentProviderStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function HasPaymentProviderStep({ onNext, onBack }: HasPaymentProviderStepProps) {
  const { billingData, setBillingData } = useOnboardingStore();
  const [hasProvider, setHasProvider] = useState<boolean | null>(billingData.hasPaymentProvider ?? null);

  useEffect(() => {
    if (hasProvider !== null) {
      setBillingData({ hasPaymentProvider: hasProvider });
    }
  }, [hasProvider, setBillingData]);

  const handleContinue = () => {
    if (hasProvider === null) return;

    if (hasProvider) {
      // They have a provider - proceed to migration strategy step
      setBillingData({ 
        provider: 'rollops',
        hasPaymentProvider: true,
      });
      onNext();
    } else {
      // No provider - new customer path
      // Set payment provider to rollops, set rolloverType to 'new'
      // Message: "Ok that makes it easy RollOps can set you up as a new Billing Platform."
      setBillingData({ 
        provider: 'rollops',
        rolloverType: 'new',
        hasPaymentProvider: false,
      });
      // Proceed directly to fee structure step
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mx-auto mb-4">
          <CreditCard size={64} className="text-rollops-accent" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Choose Your RollOps Setup Type</h2>
        <p className="text-[#D1D5DB] mt-2">Do you already have an existing payment provider/automated system today?</p>
      </div>

      <div className="space-y-4">
        <button
          onClick={() => setHasProvider(true)}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            hasProvider === true
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#EBFFEE] mb-2">Yes</h3>
              <p className="text-[#D1D5DB] text-sm">
                I&apos;m currently using a payment provider (Stripe, PayPal, etc.)
              </p>
              {hasProvider === true && (
                <p className="text-sm text-green-500 mt-2 font-semibold">
                  That&apos;s ok RollOps makes the Transition Process Super Smooth.
                </p>
              )}
            </div>
            {hasProvider === true && (
              <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm">✓</span>
              </div>
            )}
          </div>
        </button>

        <button
          onClick={() => setHasProvider(false)}
          className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
            hasProvider === false
              ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
              : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#EBFFEE] mb-2">No</h3>
              <p className="text-[#D1D5DB] text-sm">
                I don&apos;t have a payment provider set up yet
              </p>
              {hasProvider === false && (
                <p className="text-sm text-green-500 mt-2 font-semibold">
                  Ok that makes it easy RollOps can set you up as a new Billing Platform.
                </p>
              )}
            </div>
            {hasProvider === false && (
              <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm">✓</span>
              </div>
            )}
          </div>
        </button>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-[#363232] text-[#EBFFEE] rounded-lg hover:bg-[#504A4A] transition-colors"
        >
          Go Back
        </button>
        <button
          onClick={handleContinue}
          disabled={hasProvider === null}
          className={`px-6 py-3 rounded-lg transition-colors ${
            hasProvider !== null
              ? 'bg-[#9B1421] text-white hover:bg-[#C01F2D]'
              : 'bg-[#363232] text-[#D1D5DB] cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
